import {
  useEffect,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type MouseEvent,
} from "react";
import { ROUTES, type Route } from "./links";

const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  window.addEventListener("popstate", fn);
  return () => {
    listeners.delete(fn);
    window.removeEventListener("popstate", fn);
  };
};

/** Normalises "/pricing/" and "/pricing" to the same route. */
export function routeFor(pathname: string): Route {
  const path = pathname.replace(/\/+$/, "") || "/";
  const match = (Object.keys(ROUTES) as Route[]).find((r) => ROUTES[r] === path);
  return match || "home";
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, () => routeFor(location.pathname));
}

function scrollToHash(hash: string) {
  const el = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
  if (el) el.scrollIntoView({ block: "start" });
  else window.scrollTo(0, 0);
}

export function navigate(href: string) {
  const url = new URL(href, location.href);
  const samePage = url.pathname === location.pathname;
  history.pushState(null, "", url.pathname + url.search + url.hash);
  listeners.forEach((fn) => fn());
  // Let the new page render before looking for the anchor.
  requestAnimationFrame(() => {
    if (samePage && !url.hash) window.scrollTo({ top: 0 });
    else scrollToHash(url.hash);
  });
}

/** A same-origin link that changes page without a reload. */
export function Link({
  href,
  onClick,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const click = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      rest.target
    )
      return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={click} {...rest} />;
}

/** Sets the tab title for the current page. */
export function usePage(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/** Highlights the section of a long page that is currently in view. */
export function useActiveSection(ids: string[], onChange: (id: string) => void) {
  const key = ids.join("|");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const seen = new Map<string, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) seen.set(entry.target.id, entry.isIntersecting);
        const first = ids.find((id) => seen.get(id));
        if (first) onChange(first);
      },
      { rootMargin: "-15% 0px -60% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
